import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Clock, Calendar, Twitter, Link as LinkIcon, MessageCircle, Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TezBox, BetweenLinesNote } from '@/components/article/TezBox';
import { ArticleCardHorizontal } from '@/components/article/ArticleCard';
import { Reactions } from '@/components/reactions/Reactions';
import { postsApi } from '@/lib/api';
import { toast } from 'sonner';

const ArticlePage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const postData = await postsApi.getPostBySlug(slug);
      setPost(postData);
      
      // Fetch related posts (same category)
      try {
        const relatedData = await postsApi.getPublicPosts({ 
          category: postData.category, 
          limit: 4 
        });
        setRelatedPosts(relatedData.posts.filter(p => p._id !== postData._id).slice(0, 3));
      } catch (e) {
        console.error('Failed to fetch related posts:', e);
      }
      
    } catch (error) {
      console.error('Failed to fetch post:', error);
      setError('Yazı bulunamadı');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link kopyalandı!');
  };

  const shareTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post.title);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
  };

  const shareWhatsApp = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`${post.title} - TheAnalystRoom`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="container-article py-20 text-center">
          <h1 className="heading-section text-foreground mb-4">Yazı Bulunamadı</h1>
          <p className="text-muted-foreground mb-8">Aradığınız yazı mevcut değil veya kaldırılmış olabilir.</p>
          <Link to="/yazilar">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Tüm Yazılara Dön
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const publishedDate = post.published_at || post.created_at;
  const updatedDate = post.updated_at || publishedDate;

  const formattedDate = new Date(publishedDate).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const siteUrl = window.location.origin;
  const canonicalUrl = `${siteUrl}/yazi/${post.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.seo_title || post.title,
    description: post.seo_description || post.excerpt,
    datePublished: new Date(publishedDate).toISOString(),
    dateModified: updatedDate ? new Date(updatedDate).toISOString() : undefined,
    author: {
      '@type': 'Person',
      name: 'Süleyman Kapıcıoğlu',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    url: canonicalUrl,
  };

  return (
    <Layout>
      <Helmet>
        <title>{post.seo_title || post.title} | TheAnalystRoom</title>
        <meta name="description" content={post.seo_description || post.excerpt} />
        <meta property="og:title" content={post.seo_title || post.title} />
        <meta property="og:description" content={post.seo_description || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      {/* Article Header */}
      <header className="container-wide py-12 sm:py-16">
        <div className="max-w-3xl">
          {/* Back Link */}
          <Link 
            to="/yazilar" 
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            Tüm Yazılar
          </Link>

          {/* Category */}
          <div className="flex items-center gap-2 mb-4">
            <Link to={`/kategori/${post.category}`}>
              <Badge variant="outline" className="category-badge hover:bg-accent hover:text-accent-foreground transition-colors">
                {post.category}
              </Badge>
            </Link>
          </div>

          {/* Title */}
          <h1 className="heading-article text-foreground mb-4 text-balance">
            {post.title}
          </h1>

          {/* Subtitle */}
          {post.subtitle && (
            <p className="text-xl text-muted-foreground font-serif italic mb-6">
              {post.subtitle}
            </p>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Süleyman Kapıcıoğlu</span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formattedDate}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {post.reading_time} dk okuma
            </span>
          </div>

          {/* Series removed as per FINAL PROJECT COMMAND */}
        </div>
      </header>

      {/* Horizontal Rule */}
      <div className="container-wide">
        <div className="h-px bg-border" />
      </div>

      {/* Article Content */}
      <article className="container-wide py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8 xl:col-span-7">
            {/* Tez Box */}
            <TezBox>
              {post.excerpt}
            </TezBox>

            {/* Article Body */}
            <div 
              className="article-content prose-headings:scroll-mt-20"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* TheAnalystRoom Note */}
            <BetweenLinesNote>
              Bu yazı, futbolun taktik boyutunu anlamak isteyenler için hazırlandı. 
              Sahada gördüklerimizin ardındaki stratejik kararları keşfetmek, oyunu daha derinlikli görmenin ilk adımı.
            </BetweenLinesNote>

            {/* Reactions */}
            <div className="mt-10 pt-8 border-t border-border">
              <Reactions postId={post._id} />
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-10 pt-8 border-t border-border">
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-3">
                  Etiketler
                </p>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Link key={tag} to={`/yazilar?tag=${tag}`}>
                      <Badge variant="secondary" className="tag hover:tag-active transition-colors">
                        {tag}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Share Buttons */}
            <div className="mt-8 pt-8 border-t border-border">
              <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-3">
                Paylaş
              </p>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={shareTwitter}
                  className="border-border"
                >
                  <Twitter className="h-4 w-4 mr-2" />
                  X
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={shareWhatsApp}
                  className="border-border"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyLink}
                  className="border-border"
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Link
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 xl:col-span-5">
            <div className="lg:sticky lg:top-24 space-y-8">
              {/* Author */}
              <div className="p-6 bg-card border border-border rounded-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                    <span className="font-serif text-lg font-bold text-muted-foreground">SK</span>
                  </div>
                  <div>
                    <p className="font-serif font-bold text-foreground">Süleyman Kapıcıoğlu</p>
                    <p className="text-sm text-muted-foreground">Yazar & Analist</p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <>
          <div className="container-wide">
            <div className="h-px bg-border" />
          </div>

          <section className="container-wide py-12 sm:py-16">
            <h2 className="heading-subsection text-foreground mb-8">İlgili Yazılar</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <ArticleCardHorizontal key={relatedPost._id} post={relatedPost} />
              ))}
            </div>
          </section>
        </>
      )}
    </Layout>
  );
};

export default ArticlePage;
